




























































































import { HomePaths } from '@/core/constants';
import { Role } from '@/core/security/Role';
import { User } from '@/models/user/user.model';
import { AdminApi } from '@/services/api/api.admin';
import { BugTrackerModule } from '@/store/modules/bugtracker/bugtracker.store';
import { RealmsModule } from '@/store/modules/realms/realms.store';
import { UserModule } from '@/store/modules/user/user.store';
import { BugTrackerCategoryViewObject, BugTrackerIssueDetailsViewModel, BugTrackerIssueViewObject, BugTrackerPriority, BugTrackerStatus, UpdateBugTrackerIssueRequest, UserViewObject } from '@/types/apiServerContract';
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import bugtrackerService from './bugtracker.service';

@Component
export default class BugTrackerAdminControls extends Vue {
    roles: Role[] = [Role.Administrator, Role.SuperAdmin];

    @Prop({ required: true, type: Object })
    details!: BugTrackerIssueDetailsViewModel;

    @Prop({ required: true, type: Array })
    categories!: BugTrackerCategoryViewObject[];

    @Prop({ required: true, type: Boolean })
    editMode!: boolean;

    title: string = '';
    description: string = '';

    admins: UserViewObject[] = [];

    get issue(): BugTrackerIssueViewObject {
        return this.details.issue;
    }

    get user(): User {
	    return UserModule.user!;
    }

    get isAdmin() {
	    return this.user?.IsAdmin ?? false;
    }

    get isIssueOwner() {
	    return this.user != null && (this.isAdmin || this.issue?.createdBy === this.user?.id);
    }

    get isAssignedToSelf() {
        return this.request.assignee === this.user.id;
    }

    get isUnassigned() {
        return !this.request.assignee;
    }

    get bugTrackerStatusTypes() {
        const entries = Object.entries(BugTrackerStatus);
	    return entries.slice(-entries.length / 2).map(x => ({
	        name: bugtrackerService.getStatus(x[1] as BugTrackerStatus),
	        id: x[1]
	    }));
    }

    get bugTrackerPriorityTypes() {
        const entries = Object.entries(BugTrackerPriority);
	    return entries.slice(-entries.length / 2).map(x => ({
	        name: x[0].replace('BugTrackerPriority', ''),
	        id: x[1]
	    }));
    }

    get realms() {
	    return RealmsModule.realms;
    }

    toggleEdit() {
        if (!this.editMode) {
            this.title = this.issue.title;
            this.description = this.issue.description;
        } else {
            this.issue.title = this.title;
            this.issue.description = this.description;
        }

        this.$emit('toggle-edit', !this.editMode);
    }

    request: UpdateBugTrackerIssueRequest = {
        assignee: this.issue.assignee,
        issueId: this.issue.id,
        priority: this.issue.priority,
        status: this.issue.status,
        realmId: this.issue.realmId,
        categoryId: this.issue.categoryId,
        description: this.issue.description,
        title: this.issue.title,
        rewardVp: false,
        triggerSummariesBroadcast: false
    }

    assignToSelf() {
        this.request.assignee = this.user.id;
    }

    unassign() {
        this.request.assignee = undefined;
    }

    async removeIssue() {
        if (!this.isIssueOwner || this.resetting) return;

        const check = await this.$bvModal.msgBoxConfirm('Are you sure you wish to delete this issue?', {
	        centered: true,
	        okTitle: 'Yes'
	    });
	    if (!check) return;

        try {
            await BugTrackerModule.removeIssue(this.issue);
            this.$router.push(HomePaths.BugTrackerSearch);
        } finally {
            this.previousStatus = this.request.status;
        }
    }

    async updateIssue() {
        if (!this.isIssueOwner || this.resetting) return;
        if (this.title.length < 15 || this.description.length < 30) return;

        try {
            const rewardVp = this.request.status !== this.previousStatus && this.request.status === BugTrackerStatus.Fixed && this.request.rewardVp;
            await BugTrackerModule.updateIssue({ ...this.request, rewardVp, title: this.issue.title, description: this.issue.description, triggerSummariesBroadcast: this.triggerSummariesBroadcast });
            this.previousStatus = this.request.status;
            this.resetRewardVp();
            this.$emit('toggle-edit', false);
        } catch (e) {
            this.resetting = true;
            this.previousStatus = this.issue.status;
            this.request.status = this.issue.status;
            this.request.rewardVp = false;
            this.$nextTick(() => this.resetting = false);
        }
    }

    async getAdmins() {
        if (!this.isAdmin) return;
        const result = await AdminApi.GetAdmins();
        this.admins = result.users;
    }

    resetting: boolean = false;
    previousStatus: BugTrackerStatus = this.issue.status;

    get triggerSummariesBroadcast() {
        return this.previousStatus !== this.request.status;
    }

    resetRewardVp() {
        if (!this.request.rewardVp) return;

        this.resetting = true;
        this.request.rewardVp = false;
        this.$nextTick(() => this.resetting = false);
    }

    reset() {
        this.resetting = true;
        this.request = {
            assignee: this.issue.assignee,
            issueId: this.issue.id,
            priority: this.issue.priority,
            status: this.issue.status,
            realmId: this.issue.realmId,
            categoryId: this.issue.categoryId,
            description: this.issue.description,
            title: this.issue.title,
            rewardVp: false,
            triggerSummariesBroadcast: false
        };
        this.$nextTick(() => this.resetting = false);
    }

    @Watch('request', {
        deep: true
    })
    onRequestChange() {
        if (this.resetting || this.request.status !== this.previousStatus) {
            return;
        }

        this.updateIssue();
    }

    @Watch('request.status')
    async onStatusChanged(status: BugTrackerStatus, oldStatus: BugTrackerStatus) {
        if (this.resetting || status === oldStatus) return;

        if (status === BugTrackerStatus.Fixed) {
            this.resetting = true;
            const reward = await this.$bvModal.msgBoxConfirm('Reward VP to issue creator?', {
                centered: true,
                okTitle: 'Yes',
                cancelTitle: 'No'
            });
            this.request.rewardVp = reward;
            this.resetting = false;
        }

        this.updateIssue();
    }

    @Watch('issue')
    onIssueChange(issue: BugTrackerIssueViewObject, oldIssue: BugTrackerIssueViewObject) {
        if (issue.id !== oldIssue.id) {
            this.reset();
        }
    }

    created() {
        this.getAdmins();

        this.title = this.issue.title;
        this.description = this.issue.description;
    }
}
